import { Center, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import useCashFlowSummaryQuery from 'hooks/useCashFlowSummaryQuery';
import useTotalPromotionalCreditsQuery from 'hooks/useTotalPromotionalCreditsQuery';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { formatCurrency } from '../../helpers/formatCurrency';
import { Card } from '../../layout/Card';
import { ContentLayout } from '../../layout/ContentLayout';
import PageMetatags from '../../layout/PageMetatags';

const ControllingOverviewPage = () => {
  const { t } = useTranslation();

  const totalPromotionalCreditsQuery = useTotalPromotionalCreditsQuery();
  const cashFlowSummaryQuery = useCashFlowSummaryQuery();

  if (totalPromotionalCreditsQuery.isLoading || cashFlowSummaryQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentLayout>
      <PageMetatags title={t('pages:controlling.meta.title')} />

      <Heading size={'md'}>{t('pages:controlling.meta.title')}</Heading>

      <Heading size={'sm'}>{t('pages:controlling.kpi.title')}</Heading>
      <SimpleGrid columns={{ sm: 1, md: 4 }} spacing={4}>
        <Card py={4} minH={0}>
          <Center>
            <VStack textAlign={'center'}>
              {!totalPromotionalCreditsQuery.isFetching ? (
                <>
                  <Heading size={'xs'}>{t('pages:controlling.kpi.promotional_credits.label')}</Heading>
                  <Heading size={'xs'}>{formatCurrency(get(totalPromotionalCreditsQuery.data, 'amount', 0))}</Heading>
                </>
              ) : (
                <LoadingSpinner />
              )}
            </VStack>
          </Center>
        </Card>
      </SimpleGrid>

      <Heading size={'md'}>{t('pages:controlling.cashflow.title')}</Heading>
      <SimpleGrid columns={{ sm: 1, md: 4 }} spacing={4}>
        <Card py={4} minH={0}>
          <VStack>
            {!cashFlowSummaryQuery.isFetching ? (
              <>
                <Heading size={'xs'}>{t('pages:controlling.cashflow.invoices.label')}</Heading>
                <Heading size={'xs'}>{cashFlowSummaryQuery.data?.customers}</Heading>
                <Text>
                  {t('pages:controlling.cashflow.amr.label')}: {formatCurrency(get(cashFlowSummaryQuery.data, 'amr', 0))}
                </Text>
              </>
            ) : (
              <LoadingSpinner />
            )}
          </VStack>
        </Card>
        <Card py={4} minH={0}>
          <VStack>
            {!cashFlowSummaryQuery.isFetching ? (
              <>
                <Heading size={'xs'}>{t('pages:controlling.cashflow.billing_amount.label')}</Heading>
                <Heading size={'xs'}>
                  {formatCurrency(get(cashFlowSummaryQuery.data, 'unbilledCharges', 0) + get(cashFlowSummaryQuery.data, 'renewals', 0))}
                </Heading>
                <Text>
                  {t('pages:controlling.cashflow.subscriptions.label')}: {formatCurrency(get(cashFlowSummaryQuery.data, 'renewals', 0))}
                </Text>
                <Text>
                  {t('pages:controlling.cashflow.charges.label')}: {formatCurrency(get(cashFlowSummaryQuery.data, 'unbilledCharges', 0))}
                </Text>
              </>
            ) : (
              <LoadingSpinner />
            )}
          </VStack>
        </Card>
        <Card py={4} minH={0}>
          <VStack textAlign={'center'}>
            {!cashFlowSummaryQuery.isFetching ? (
              <>
                <Heading size={'xs'}>{t('pages:controlling.cashflow.estimated_realization.label')}</Heading>
                <Heading size={'xs'}>{formatCurrency(get(cashFlowSummaryQuery.data, 'estimatedRealization', 0))}</Heading>
                <Text size={'xs'}>
                  von insgesamt
                  <br />
                  {formatCurrency(get(cashFlowSummaryQuery.data, 'totalPromotionalCredits', 0))}
                </Text>
              </>
            ) : (
              <LoadingSpinner />
            )}
          </VStack>
        </Card>
        <Card py={4} minH={0}>
          <VStack textAlign={'center'}>
            {!cashFlowSummaryQuery.isFetching ? (
              <>
                <Heading size={'xs'}>{t('pages:controlling.cashflow.estimated_cashflow.label')}</Heading>
                <Heading size={'xs'}>{formatCurrency(get(cashFlowSummaryQuery.data, 'estimatedCashflow', 0))}</Heading>
              </>
            ) : (
              <LoadingSpinner />
            )}
          </VStack>
        </Card>
      </SimpleGrid>
    </ContentLayout>
  );
};

export default ControllingOverviewPage;
